import Immutable from 'seamless-immutable';

import { mergeRedux } from 'util/saving';
import { FAIL, SUCCESS } from '../../constants/chart-constants';
import documentTemplate from '../../constants/document-template';
import { convertAll, createObj, newReducers } from '../../constants/new-reducers';
import sortDataByDate from '../../util/sortDataByDate';
import validChartDocument from '../../util/validChartDocument';

let uniqueNum = 0;
// let untitledNum = 0;

// TODO: move to module
// Creates a unique ID for this chart inside the UI (not the server DB).
function getUniqueId() {
  return `chart-${uniqueNum++}`;
}

// Generates a unique document ID for a new chart and adds the new chart to
// the items hash.
function addChart(state, chart) {
  const uid = getUniqueId();
  state = state.set('active', uid);
  chart = chart.set('documentId', uid);
  chart = sortDataByDate(chart);
  return state.setIn(['items', state.active], chart);
}

function createChart(state) {
  const chart = validChartDocument();
  return addChart(state, chart);
}

function importChart(state, action) {
  const chart = validChartDocument(action.chart);
  state = addChart(state, chart);
  return state;
}

function deleteChart(state, action) {
  if (action.error || action.data.status === FAIL) {
    if (action.data && action.data.id) {
      state.setIn(
        ['items', action.data.id, 'chartIo', 'error'],
        action.error || `An error occurred while deleting chart "${action.data.id}".`
      );
    }
  } else if (action.data.status === SUCCESS) {
    for (let i = 0, keys = Object.keys(state.items); i < keys.length; i++) {
      const item = state.items[keys[i]];

      if (item.id === action.data.id) {
        state = state.set('items', state.items.without(keys[i]));
      }
    }
  }
  return state;
}

function requestChart(state, action) {
  let chart = Immutable(documentTemplate);
  chart = chart.set('id', action.id);
  chart = chart.setIn(['chartIo', 'fetching'], true);
  return addChart(state, chart);
}

// new functions
function handleError(state, action, errorMessage) {
  // TODO: review using error handling in multiple cases with inconsistent action payload
  if (!action.data || !action.data || !action.data.body._id) {
    return state;
  }

  // eslint-disable-next-line no-underscore-dangle
  const id = action.data.body._id;

  // TODO: review immutable usage
  state = state.setIn(['items', id, 'chartIo', 'error'], action.error || `${errorMessage + id}".`);
  state = state.setIn(['items', id, 'chartIo', 'fetching'], false);

  return state;
}

function docById(state, id) {
  for (let i = 0, keys = Object.keys(state.items); i < keys.length; i++) {
    if (state.items[keys[i]].id === id) {
      return state.items[keys[i]];
    }
  }
  return null;
}

function createChartCompleteDuplicate(state, action) {
  window.location = `/${getPublication()}/chart/${action.id}`;
}

function receiveChart(state, action) {
  if (action.error || action.data.status === FAIL) {
    if (action.data && action.data.body && action.data.body._id) {
      state = handleError(state, action, 'An error occurred while loading chart "');
    }
  } else if (action.data.status === SUCCESS) {
    const chartData = action.data.body.chart.data;
    const propsToMerge = convertAll(chartData, ...newReducers);
    let body = validChartDocument(action.data.body);
    body = mergeRedux(body, ...propsToMerge);
    let item = null;
    for (let i = 0, keys = Object.keys(state.items); i < keys.length; i++) {
      if (state.items[keys[i]].id === body.id) {
        item = body.set('documentId', keys[i]);
        break;
      }
    }
    if (item) {
      item = item.setIn(['chartIo', 'fetching'], false);
      item = sortDataByDate(item);
      //update series order on the ui for charts created before 'legend items in order' bugfix
      if (
        item.chart.data.itemsInOrder &&
        item.chart.data.series.length > 1 &&
        item.chart.data.series[0].initialIndex === undefined
      ) {
        const series = item.chart.data.series.asMutable({ deep: true });
        series.forEach((series, i) => {
          series.initialIndex = i;
        });
        series.sort((a, b) => b.data[b.data.length - 1].y - a.data[a.data.length - 1].y);

        item = item.setIn(['chart', 'data', 'series'], series);
      }
      state = state.setIn(['items', item.documentId], item);
      state = state.set('active', item.documentId);
    } else {
      state = addChart(state, body);
    }
    const propsObj = createObj(chartData, ...newReducers);
    state = state.merge(propsObj);
  }
  return state;
}

function saveChartComplete(state, action) {
  if (action.error || action.data.status !== SUCCESS) {
    state = handleError(state, action, 'An error occurred while saving chart "');
  } else if (action.data.status === SUCCESS) {
    const chartData = action.data.body.chart.data;
    const propsToMerge = convertAll(chartData, ...newReducers);

    let validDoc = validChartDocument(action.data.body, true, false);
    validDoc = mergeRedux(validDoc, ...propsToMerge);

    let existDoc = docById(state, validDoc.id);

    if (existDoc === null && state.active) {
      existDoc = state.items[state.active];
    }
    if (existDoc) {
      validDoc = validDoc.set('documentId', existDoc.documentId);
      validDoc = validDoc.set('svg', existDoc.svg);
      validDoc = validDoc.set('svgAi', existDoc.svgAi);
      validDoc = validDoc.set('svgBmp', existDoc.svgBmp);
      state = state.setIn(['items', existDoc.documentId], validDoc);
    }
  }
  return state;
}

function publishStart(state, action) {
  return state.setIn(['items', action.id, 'publishIo'], {
    status: 'Publishing started',
    error: null,
  });
}

function publishComplete(state, action) {
  state = state.setIn(['items', action.id, 'publishIo'], {
    status: null,
    error: action.error,
  });
  return saveChartComplete(state, action);
}

function publishStep(state, action) {
  let doc = docById(state, action.data.id).set('publishIo', {
    status: action.data.status,
    error: null,
  });
  if (action.data.chart) {
    doc = doc.setIn(['history'], action.data.chart.history);
    doc = doc.setIn(['urls'], action.data.chart.urls);
  }
  return state.setIn(['items', doc.documentId], doc);
}

// #region UNUSED
// TODO: review for deletion
function publishInsetState(state, action) {
  // @ts-ignore
  state = copyState(state);
  let id = action.documentId || state.active;
  let doc = state.items[id];
  if (doc) {
    doc = Object.assign({}, doc);
    doc.chartIo = Object.assign({}, doc.chartIo);
    doc.chartIo.posting = true;
    state.items[id] = doc;
  }
  return state;
}

function publishInsetComplete(state, action) {
  // error data
  if (action.error && action.data.status !== SUCCESS) {
    if (action.data && action.data.body && action.data.body._id) {
      state = state.setIn(
        ['items', action.data.body._id, 'chartIo', 'error'],
        action.error || 'An error occurred while publishing chart "' + action.data.body._id + '".'
      );
      state = state.setIn(['items', action.data.body._id, 'chartIo', 'posting'], false);
    }
  } else if (action.data.status === SUCCESS) {
    let validDoc = validChartDocument(action.data.body);
    let existDoc = docById(state, validDoc.id);
    if (!existDoc && state.active) {
      existDoc = state.items[state.active];
    }
    if (existDoc) {
      state = state.setIn(
        ['items', existDoc.documentId],
        validDoc.set('documentId', existDoc.documentId)
      );
    }
  }
  return state;
}

function getMustRenderIndex(mustRender, id) {
  if (mustRender) {
    for (let i = 0; i < mustRender.length; i++) {
      if (mustRender[i].id === id) {
        return i;
      }
    }
  }
  return -1;
}
// #endregion

export {
  createChart,
  importChart,
  deleteChart,
  requestChart,
  receiveChart,
  createChartCompleteDuplicate,
  saveChartComplete,
  publishStart,
  publishStep,
  publishComplete,
};
